import clsx from 'clsx';
import React from 'react';
import ChevronLeftIcon from '../../assets/icons/chevron-down-icon.svg?react';
import Button from '../Button';
import {
  CorrectiveActionsPriorityEnum,
  GetCorrectiveActionAssignedUsingIdResponse,
} from '@EHSNavigator/backend';
import { formatDate } from '../../utils/dateFormat';
import { formatTime } from '../../utils/timeFomat';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import AssignedCorrectiveActionsDetailBanner from '../AssignedCorrectiveActionsDetailBanner';

interface AssignedCorrectiveActionsDetailStepTwoProps {
  otherClasses?: string;
  handleSteps: React.Dispatch<number>;
  formHandler: any;
  data: GetCorrectiveActionAssignedUsingIdResponse['correctiveAction'];
  departments: { label: string; value: string }[];
  userList: { label: string; value: string }[];
}

const AssignedCorrectiveActionsDetailStepTwo: React.FC<
  AssignedCorrectiveActionsDetailStepTwoProps
> = ({
  otherClasses,
  data,
  departments,
  formHandler,
  handleSteps,
  userList,
}) => {
  const assignedCorrectiveActionsDetailStepTwoClasses = clsx(otherClasses);

  let updatedDepartments = [...departments];
  const exists = departments?.some(
    (node) => node.value === data?.department?.id,
  );
  if (!exists && data?.department?.id) {
    updatedDepartments.push({
      label: data?.department?.name || '',
      value: data?.department?.id,
    });
  }
  return (
    <div
      className={assignedCorrectiveActionsDetailStepTwoClasses}
      data-testid="assigned-corrective-actions-detail-step-two"
    >
      <div className="flex flex-col items-start gap-2.5">
        <Button
          type="button"
          onClick={() => handleSteps(2)}
          variant="link"
          otherClasses="gap-2.5 w-fit "
        >
          <span className="bg-brand-byzantine rounded-5 flex h-8 w-8 items-center justify-center">
            <ChevronLeftIcon className="h-7 w-7 rotate-90 [&>path]:fill-white" />
          </span>
          <strong>Return</strong>
        </Button>

        <h1 className="h3 mb-1 lg:mb-0">Review Corrective Action</h1>
        <p className="text-brand-byzantine font-inter-variable -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
          STEP 2 / 2
        </p>
      </div>
      <AssignedCorrectiveActionsDetailBanner data={data} />
      <div className="pb-6 lg:pb-9">
        <div className="grid grid-cols-1 items-start justify-between gap-6 md:grid-cols-3 md:gap-10 lg:justify-start lg:gap-5 2xl:gap-10">
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
              Due Date & Time
            </h6>
            <div className="flex items-start gap-75 md:gap-12 lg:gap-6 2xl:gap-75">
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Date
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                  {formatDate(formHandler?.values?.date)}
                </p>
              </div>
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Time
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                  {formatTime(formHandler?.values?.time)}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
              Assignee
            </h6>
            <div className="grid grid-cols-2 items-start gap-6 lg:grid-cols-1 xl:grid-cols-2 2xl:gap-x-60">
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Department
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                  {
                    updatedDepartments?.find(
                      (node) => node?.value === formHandler?.values?.department,
                    )?.label
                  }
                </p>
              </div>
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Assignee
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal capitalize">
                  {formHandler?.values?.assignee
                    ?.map(
                      (item: any) =>
                        userList?.find((user) => user.value === item)?.label,
                    )
                    .join(', ')}
                </p>
              </div>
              {formHandler?.values?.leadAssignee && (
                <div className="col-span-2 flex flex-col gap-2.5 lg:col-span-1 xl:col-span-2">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                    Lead Assignee
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal capitalize">
                    {
                      userList?.find(
                        (user) =>
                          user.value === formHandler?.values?.leadAssignee,
                      )?.label
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
              Priority
            </h6>
            <h3
              className={clsx(
                'uppercase',
                formHandler?.values?.actionPriority ===
                  CorrectiveActionsPriorityEnum.Urgent
                  ? 'text-brand-posh-pink'
                  : formHandler?.values?.actionPriority ===
                      CorrectiveActionsPriorityEnum.High
                    ? 'text-brand-fair-orange'
                    : formHandler?.values?.actionPriority ===
                        CorrectiveActionsPriorityEnum.Medium
                      ? 'text-primary-yellow'
                      : 'text-brand-papery',
              )}
            >
              {formHandler?.values?.actionPriority}
            </h3>
          </div>
        </div>
      </div>
      <div className="border-y-neutral-tones-text/10 border-y py-8">
        <div className="flex max-w-625 flex-col">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Action Title
          </h6>
          <p className="text-neutral-tones-text -tracking-form-input font-inter-variable text-base leading-5 font-normal capitalize italic">
            {formHandler?.values?.actionTitle}
          </p>
        </div>
        <div className="mt-8 flex max-w-625 flex-col">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Action Description
          </h6>
          <p className="text-neutral-tones-text -tracking-form-input font-inter-variable text-base leading-5 font-normal capitalize italic">
            {formHandler?.values?.description}
          </p>
        </div>
      </div>
      <div className="mt-12 flex items-center gap-8">
        <Button
          onClick={() => handleSteps(2)}
          size={useButtonSizeLarge()}
          variant="secondary"
          otherClasses="max-w-211 w-full"
        >
          <ChevronIcon className="h-5 w-5 rotate-90 [&>path]:fill-current" /> Go
          Back
        </Button>
        <Button
          size={useButtonSizeLarge()}
          otherClasses="max-w-211 w-full"
          loading={formHandler?.isSubmitting}
          disable={formHandler?.isSubmitting}
          onClick={() => formHandler?.handleSubmit()}
        >
          Complete{' '}
          <ChevronIcon className="h-5 w-5 -rotate-90 [&>path]:fill-current" />
        </Button>
      </div>
    </div>
  );
};

export default AssignedCorrectiveActionsDetailStepTwo;
